import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/apiService';

const AICorrectionsTab = ({ activeTab }) => {
    const [corrections, setCorrections] = useState([]);
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);

    // Form State
    const [contextTrigger, setContextTrigger] = useState('');
    const [incorrectInfo, setIncorrectInfo] = useState('');
    const [correctionDirective, setCorrectionDirective] = useState('');
    const [assignedAgent, setAssignedAgent] = useState('Global');

    useEffect(() => {
        if (activeTab === 'corrections') {
            fetchCorrections();
            fetchAgents();
        }
    }, [activeTab]);

    const fetchAgents = async () => {
        try {
            const data = await apiService.makeRequest('/admin/agents');
            setAgents(data.filter(a => a.is_active));
        } catch (error) {
            console.error(error);
        }
    };

    const fetchCorrections = async () => {
        setLoading(true);
        try {
            const data = await apiService.makeRequest('/reputation/corrections');
            setCorrections(data);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load AI corrections");
        } finally {
            setLoading(false);
        }
    };

    const handleAddCorrection = async (e) => {
        e.preventDefault();
        if (!contextTrigger || !incorrectInfo || !correctionDirective) {
            toast.error("Please fill out all required fields.");
            return;
        }

        try {
            const payload = {
                context_trigger: contextTrigger,
                incorrect_info: incorrectInfo,
                correction_directive: correctionDirective,
                assigned_agent: assignedAgent
            };

            await apiService.makeRequest('/reputation/corrections', {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            toast.success("Correction rule added successfully!");
            setContextTrigger('');
            setIncorrectInfo('');
            setCorrectionDirective('');
            fetchCorrections();
        } catch (error) {
            console.error(error);
            toast.error("Failed to add correction.");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this rule?")) return;
        try {
            await apiService.makeRequest(`/reputation/corrections/${id}`, {
                method: 'DELETE'
            });
            toast.success("Rule disabled.");
            fetchCorrections();
        } catch (error) {
            toast.error("Failed to delete.");
        }
    };

    if (activeTab !== 'corrections') return null;

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '30px' }}>
            <div className="card" style={{ background: '#fff', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b', marginBottom: '10px' }}>🛡️ The Correction Manager</h2>
                <p style={{ color: '#64748b', marginBottom: '20px' }}>
                    Distribute Hallucination-Correction rules to your AI Squad. If Elena or Ava make mistakes, add explicit guardrails here.
                </p>

                <form onSubmit={handleAddCorrection} style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '30px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#475569' }}>Trigger Context (Keyword/Topic)</label>
                            <input
                                type="text"
                                value={contextTrigger}
                                onChange={(e) => setContextTrigger(e.target.value)}
                                placeholder="e.g. 'URL конкурента' or '*'"
                                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#475569' }}>Target Area (Squad Member)</label>
                            <select
                                value={assignedAgent}
                                onChange={(e) => setAssignedAgent(e.target.value)}
                                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff' }}
                            >
                                <option value="Global">Global (All Agents)</option>
                                {agents.map(agent => (
                                    <option key={agent.id} value={agent.name}>{agent.name} ({agent.specialized_role})</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#475569' }}>Incorrect Info / Hallucination</label>
                        <input
                            type="text"
                            value={incorrectInfo}
                            onChange={(e) => setIncorrectInfo(e.target.value)}
                            placeholder="e.g. 'yandex.ru/abc URL is missing'"
                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#475569' }}>Explicit Directive</label>
                        <textarea
                            value={correctionDirective}
                            onChange={(e) => setCorrectionDirective(e.target.value)}
                            placeholder="e.g. 'Never use this URL. It returns 404.'"
                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', height: '80px', resize: 'vertical' }}
                        />
                    </div>

                    <button type="submit" style={{ padding: '10px 20px', borderRadius: '6px', background: '#3b82f6', color: '#fff', border: 'none', fontWeight: 'bold', cursor: 'pointer' }}>
                        + Add Correction Rule
                    </button>
                </form>

                <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e293b', marginBottom: '15px' }}>Active Rules ({corrections.length})</h3>
                {loading ? <p>Loading...</p> : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: '14px', textAlign: 'left' }}>
                                <th style={{ padding: '10px' }}>Context</th>
                                <th style={{ padding: '10px' }}>Target</th>
                                <th style={{ padding: '10px' }}>Mistake</th>
                                <th style={{ padding: '10px' }}>Rule</th>
                                <th style={{ padding: '10px' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {corrections.map((rule) => (
                                <tr key={rule.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '10px', fontWeight: '600' }}>{rule.context_trigger}</td>
                                    <td style={{ padding: '10px' }}>
                                        <span style={{ background: rule.assigned_agent === 'Global' ? '#f1f5f9' : '#e0e7ff', color: rule.assigned_agent === 'Global' ? '#475569' : '#4f46e5', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>
                                            {rule.assigned_agent}
                                        </span>
                                    </td>
                                    <td style={{ padding: '10px', color: '#ef4444', fontSize: '13px' }}>{rule.incorrect_info}</td>
                                    <td style={{ padding: '10px', color: '#10b981', fontSize: '13px' }}>{rule.correction_directive}</td>
                                    <td style={{ padding: '10px' }}>
                                        <button onClick={() => handleDelete(rule.id)} style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Disable</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default AICorrectionsTab;
